
import { defineComponent, ref } from "vue";
import Search from "@/components/Search.vue";
import AnimeBox from "@/components/AnimeBox.vue";
import CompareArrow from "@/assets/icons/CompareArrow.vue";
import ComparisonBox from "@/components/ComparisonBox.vue";
import { IAnime, IAnimeNode } from "@/libs/interfaces/Anime";
import { ISeiyuu, ISeiyuuFound, ISeiyuuShort } from "@/libs/interfaces/Seiyuu";
import { staffQuery } from "../libs/queries/Staff";
import { IStaffCharacter } from "@/libs/interfaces/Character";
import Throbber from "@/components/Throbber.vue";

export default defineComponent({
  name: "Home",
  components: {
    Search,
    CompareArrow,
    AnimeBox,
    ComparisonBox,
    Throbber
  },
  setup() {
    const animeOne = ref({} as IAnime);
    const animeTwo = ref({} as IAnime);
    const matchingVAs = ref([] as Array<ISeiyuuFound>);
    const loading = ref(false);
    const firstLoad = ref(true);

    const checkLocalStorage = () => {
      const test = "test";
      try {
        localStorage.setItem(test, test);
        localStorage.removeItem(test);
        return true;
      } catch (e) {
        return false;
      }
    };

    const timeout = (ms: number) => {
      return new Promise(resolve => setTimeout(resolve, ms));
    };

    const getVAData = async (
      vaId: number,
      animeIds: Array<number>,
      page = 1,
      foundCharacters: Array<IStaffCharacter> = [],
      foundAnime: Array<IAnimeNode> = []
    ): Promise<ISeiyuuFound> => {
      const response = await fetch("https://graphql.anilist.co", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify({
          query: staffQuery,
          variables: {
            id: vaId,
            page: page
          }
        })
      });

      const data: ISeiyuu = await response.json().then(json => json.data.Staff);

      for (const character of data.characters.nodes) {
        for (const anime of character.media.nodes) {
          if (animeIds.includes(anime.id)) {
            if (!foundCharacters.some(found => found.name.full === character.name.full)) {
              foundCharacters.push(character);
            }
            if (!foundAnime.some(found => found.id === anime.id)) {
              foundAnime.push({ ...anime, characters: [] });
            }
            if (foundAnime.length === animeIds.length) break;
          }
        }
      }

      if (data.characters.pageInfo.hasNextPage) {
        if (response.status === 429) {
          await timeout(5000);
          page = data.characters.pageInfo.currentPage;
        } else {
          page = data.characters.pageInfo.currentPage + 1;
        }
        return getVAData(vaId, animeIds, page, foundCharacters, foundAnime);
      }

      for (const anime of foundAnime) {
        for (const character of foundCharacters) {
          if (character.media.nodes.some(media => media.id === anime.id)) {
            anime["characters"].push(character);
          }
        }
      }

      const seiyuu: ISeiyuuFound = {
        ...data,
        characterMedia: foundAnime.reverse()
      };

      return seiyuu;
    };

    const compare = async () => {
      loading.value = true;
      if (Object.keys(animeOne.value).length === 0 && Object.keys(animeTwo.value).length === 0) return;

      const charactersOne = animeOne.value.characters.edges;
      const charactersTwo = animeTwo.value.characters.edges;

      const vaOne: Array<ISeiyuuShort[]> = [];
      const vaTwo: Array<ISeiyuuShort[]> = [];

      charactersOne.forEach(character => vaOne.push(character.voiceActors));
      charactersTwo.forEach(character => vaTwo.push(character.voiceActors));

      const matchingVAIds: Array<ISeiyuuShort> = [];
      vaOne.forEach(va1 => {
        vaTwo.forEach(va2 => {
          const match = va1.filter(va11 => va2.some(va22 => va11.id === va22.id));
          if (match.length > 0) {
            const found = matchingVAIds.some(va => va.id === match[0].id);
            if (!found) matchingVAIds.push(match[0]);
          }
        });
      });

      const vaData = await Promise.all(
        matchingVAIds.map(async va => {
          const idList =
            animeOne.value.id == animeTwo.value.id ? [animeOne.value.id] : [animeOne.value.id, animeTwo.value.id];
          return await getVAData(va.id, idList);
        })
      );

      matchingVAs.value = vaData;
      loading.value = false;
      firstLoad.value = false;

      if (checkLocalStorage()) {
        localStorage.setItem("animeOne", JSON.stringify(animeOne.value));
        localStorage.setItem("animeTwo", JSON.stringify(animeTwo.value));
        localStorage.setItem("matchingVAs", JSON.stringify(vaData));
      }
    };

    const onLoad = () => {
      if (checkLocalStorage()) {
        animeOne.value = JSON.parse(localStorage.getItem("animeOne") || "{}");
        animeTwo.value = JSON.parse(localStorage.getItem("animeTwo") || "{}");
        matchingVAs.value = JSON.parse(localStorage.getItem("matchingVAs") || "{}");
      }
    };

    onLoad();
    return { animeOne, animeTwo, matchingVAs, compare, loading, firstLoad };
  }
});
